"use client";

import { useActionState } from "react";
import Link from "next/link";
import { loginAction, registerAction } from "@/app/actions/auth";

type Mode = "login" | "register";

export function AuthForm({
  mode,
  next = "",
  googleEnabled = false,
}: {
  mode: Mode;
  next?: string;
  googleEnabled?: boolean;
}) {
  const action = mode === "login" ? loginAction : registerAction;
  const [state, formAction, pending] = useActionState(action, null);
  const googleHref = `/api/auth/google${next ? `?next=${encodeURIComponent(next)}` : ""}`;

  return (
    <form action={formAction} className="space-y-4">
      {next ? <input type="hidden" name="next" value={next} /> : null}
      {mode === "register" ? (
        <>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-300">Nama</span>
            <input
              name="name"
              required
              className="w-full rounded-xl border border-white/10 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/40"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-300">WhatsApp / HP</span>
            <input
              name="phone"
              className="w-full rounded-xl border border-white/10 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/40"
            />
          </label>
        </>
      ) : null}
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-slate-300">Email</span>
        <input
          name="email"
          type="email"
          required
          className="w-full rounded-xl border border-white/10 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/40"
        />
      </label>
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-slate-300">Password</span>
        <input
          name="password"
          type="password"
          required
          className="w-full rounded-xl border border-white/10 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/40"
        />
      </label>
      {state?.error ? (
        <p className="rounded-xl bg-rose-500/15 px-3 py-2 text-sm text-rose-300">{state.error}</p>
      ) : null}
      <button
        type="submit"
        disabled={pending}
        className="w-full rounded-xl bg-teal-400 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-300 disabled:opacity-60"
      >
        {pending ? "Memproses..." : mode === "login" ? "Masuk" : "Buat akun"}
      </button>
      {googleEnabled ? (
        <>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="h-px flex-1 bg-white/10" />
            atau
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <a
            href={googleHref}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-slate-950 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5"
          >
            <GoogleMark />
            {mode === "login" ? "Masuk dengan Google" : "Daftar dengan Google"}
          </a>
        </>
      ) : null}
      <p className="text-center text-sm text-slate-400">
        {mode === "login" ? (
          <>
            Belum punya akun?{" "}
            <Link href="/register" className="font-medium text-teal-300">
              Daftar
            </Link>
          </>
        ) : (
          <>
            Sudah punya akun?{" "}
            <Link href="/login" className="font-medium text-teal-300">
              Masuk
            </Link>
          </>
        )}
      </p>
      {mode === "register" ? (
        <p className="text-center text-xs text-slate-400">
          Dengan daftar, Anda menyetujui{" "}
          <Link href="/tos" className="font-medium text-teal-300 hover:underline">
            syarat dan ketentuan
          </Link>
          .
        </p>
      ) : null}
    </form>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path
        fill="#EA4335"
        d="M12 10.2v3.6h5.1c-.2 1.2-.9 2.2-1.9 2.9l3.1 2.4c1.8-1.7 2.8-4.1 2.8-7 0-.7-.1-1.3-.2-1.9H12z"
      />
      <path
        fill="#34A853"
        d="M6.6 14.3l-.8.6-2.3 1.8C5.2 19.4 8.4 21.4 12 21.4c2.4 0 4.5-.8 6.1-2.2l-3.1-2.4c-.9.6-2 .9-3 .9-2.3 0-4.3-1.5-5-3.6z"
      />
      <path
        fill="#4A90E2"
        d="M3.5 7.3C2.7 8.9 2.2 10.6 2.2 12.4s.5 3.5 1.3 5.1C5.2 19.4 8.4 21.4 12 21.4v-3.6c-3.6 0-6.5-2.9-6.5-5.4 0-.9.2-1.7.6-2.5z"
      />
      <path
        fill="#FBBC05"
        d="M12 5.4c1.3 0 2.5.5 3.4 1.3l2.5-2.5C16.4 2.7 14.4 1.8 12 1.8 8.4 1.8 5.2 3.8 3.5 7.3l3.1 2.4C7.3 6.9 9.4 5.4 12 5.4z"
      />
    </svg>
  );
}
